import jwt_decode, { JwtPayload } from "jwt-decode"
import { nanoid } from "nanoid/non-secure"
import { Platform } from "react-native"
import * as Sentry from "sentry-expo"

import { createEnvironment } from "./environment"
import { createStorage } from "./storage"

type AuthResponseSuccess = {
  token: string
  data: {
    userID: string
    accountID: string
  }
  roles: string
}

type AuthResponse = AuthResponseSuccess | { error: any }

export type PuzmoClient = ReturnType<typeof createPuzmoClient>

export const createPuzmoClient = (triggerReRender: () => void) => {
  // console.log("Creating API client")

  const env = createEnvironment()
  const storage = createStorage()

  const apiURL = (path: string) => `${env.apiRoot()}${path}`

  let authData: undefined | AuthResponseSuccess = undefined
  let userIDOrAnonID: string = ""
  let puzmoGameplayID: string = ""

  // This gets filled in during launch
  // let currentUser: CurrentUser = undefined as unknown as CurrentUser

  // Ensure that you cannot call refresh multiple times simultaneously
  let refreshPromise: undefined | Promise<unknown> = undefined

  let setup = false

  const setAuthData = async (data: AuthResponseSuccess | undefined) => {
    authData = data
    userIDOrAnonID = data?.data.userID ?? userIDOrAnonID

    if (authData) {
      await storage.set("puzmoAuth", JSON.stringify(authData))
    } else {
      await storage.rm("puzmoAuth")
      await storage.rm("puzmoUserModel")
    }
    triggerReRender()
  }

  return {
    apiRoot: env.apiRoot,
    graphQL: env.graphQL,
    environment: env,

    // /** The main way to get info on a user! */
    // user: () => {
    //   if (!currentUser) throw new Error("No current 'user' set up, this is wrong, even logged out sessions have a user!")
    //   return currentUser
    // },

    setup: async () => {
      await env.setupDevOverrides()

      const clientID = await storage.get("puzmoGameplayIDFallback")

      if (!clientID || clientID === "") {
        userIDOrAnonID = nanoid()
        await storage.set("puzmoGameplayIDFallback", userIDOrAnonID)
      } else {
        puzmoGameplayID = clientID
        userIDOrAnonID = clientID
      }

      const authInfo = await storage.get("puzmoAuth")
      if (authInfo) {
        try {
          authData = JSON.parse(authInfo) as AuthResponseSuccess
          userIDOrAnonID = authData!.data.userID
        } catch (error) {
          console.error("Failed to parse auth info", error)
        }
      }

      setup = true
    },
    isSetup: () => setup,

    authed: () => !!authData,

    login: (input: string, password: string, username?: string, usernameID?: string) => {
      if (input.includes("#")) {
        username = input.split("#")[0]
        usernameID = input.split("#")[1]
      }
      return fetch(apiURL("jwtLogin"), {
        method: "POST",
        body: JSON.stringify({ input: input.toLowerCase(), password, username, usernameID, "puzzmo-gameplay-id": puzmoGameplayID }),
        credentials: "include",
      })
        .then((res) => res.json() as Promise<AuthResponse | { userInfoAccessToken: string }>)
        .then(async (json) => {
          if ("error" in json) throw new Error(json.error.message)

          // This means you've got to handle selecting a user for the account, so we don't store
          // the details yet - because you'll be hitting login again with a 'username' param.
          if ("userInfoAccessToken" in json) {
            return json
          }

          if (json?.token) {
            await setAuthData(json)
            return json
          }
        })
    },

    // Like login, but with no side-effect
    validateCredentials: (username: string, usernameID: string, password: string) =>
      fetch(apiURL("jwtLogin"), {
        method: "POST",
        body: JSON.stringify({ password, input: username, username, usernameID, noSession: true }),
        credentials: "include",
      })
        .then((res) => res.json() as Promise<AuthResponse>)
        .then((json) => {
          return !("error" in json)
        }),

    switchUser: (username: string, usernameID: string) =>
      fetch(apiURL("jwtUserSwitch"), {
        method: "POST",
        body: JSON.stringify({ username, usernameID }),
        credentials: "include",
      })
        .then((res) => res.json() as Promise<AuthResponse>)
        .then(async (json) => {
          if ("error" in json) throw new Error(json.error.message)

          if (json?.token) {
            await setAuthData(json)
            return json
          }
        }),

    adminLoginAsUser: (username: string, usernameID: string, email: string, password: string) =>
      fetch(apiURL("jwtLogin"), {
        method: "POST",
        body: JSON.stringify({ username, usernameID, input: email, password }),
        credentials: "include",
      })
        .then((res) => res.json() as Promise<AuthResponse>)
        .then((json) => {
          if ("error" in json) throw new Error(json.error.message)

          if (json?.token) {
            setAuthData(json)
            return json
          }
        }),

    // Note: no usernameID is intentional here, as the first time is server-set
    signup: (info: {
      email: string
      password: string
      username: string
      avatarIndex: number
      displayName: string
      partnerSlug: string | undefined
    }) =>
      fetch(apiURL("jwtSignup"), { method: "POST", body: JSON.stringify({ ...info, "puzzmo-gameplay-id": puzmoGameplayID }) })
        .then((res) => res.json() as Promise<AuthResponse>)
        .then((json) => {
          if ("error" in json) throw new Error(json.error)

          if (json?.token) {
            setAuthData(json)

            return json
          }
        }),

    logout: (skipRedirect?: true) => {
      // if (!authData) throw new Error("No user data to log out")

      // const token = authData.token
      return fetch(apiURL("jwtLogout"), {
        method: "POST",
        body: JSON.stringify({}),
        credentials: "include",
      })
        .then((res) => res.json() as Promise<AuthResponse>)
        .then((json) => {
          return json
        })
        .catch(() => {
          // OK
        })
        .finally(async () => {
          await setAuthData(undefined)

          // TODO: Hack
          if (!skipRedirect && Platform.OS === "web") {
            window.location.reload()
          }
        })
    },

    refresh: () => {
      // There should only ever be a single refresh in progress at a time
      // if (refreshPromise) {
      //   Sentry.Browser.addBreadcrumb({
      //     category: "auth",
      //     message: `Waiting on current refresh to resolve`,
      //     level: "info",
      //   })

      //   return refreshPromise
      // }

      // // It's pretty trivial to have multiple tabs open with Puzzmo open, so we want to
      // // support refreshing when many tabes are open. We do this by using localStorage
      // // to show a lock, and then waiting for the lock to be removed.

      // if (Platform.OS === "web") {
      //   if (localStorage.getItem("refresh-lock")) {
      //     Sentry.Browser.addBreadcrumb({
      //       category: "auth",
      //       message: `Waiting on current refresh to resolve via localStorage lock`,
      //       level: "info",
      //     })

      //     return new Promise((resolve, reject) => {
      //       const interval = setInterval(async () => {
      //         const refreshLock = localStorage.getItem("refresh-lock")
      //         if (!refreshLock) {
      //           clearInterval(interval)

      //           const authData = JSON.parse(localStorage.getItem("puzmoAuth")!)
      //           await setAuthData(authData)
      //           resolve(undefined)
      //         } else {
      //           const lockStarted = new Date(refreshLock)
      //           const fifteenSecondsAgo = new Date(new Date().getTime() - 15000)

      //           if (lockStarted.getTime() < fifteenSecondsAgo.getTime()) {
      //             clearInterval(interval)
      //             localStorage.removeItem("refresh-lock")
      //             Sentry.Browser.addBreadcrumb({
      //               category: "auth",
      //               message: `Refresh via localStorage lock timed out`,
      //               level: "info",
      //             })
      //             reject(new Error("Refresh lock timed out"))
      //           }
      //         }
      //       }, 100)
      //     })
      //   }
      // }

      // // Temporarily wait on all network requests on getting a new refresh token
      // refreshPromise = new Promise(async (resolve, reject) => {
      //   if (!authData) throw new Error("No user data available during a JWT refresh")
      //   if (Platform.OS === "web") localStorage.setItem("refresh-lock", new Date().toISOString())

      //   const refreshToken = authData.token
      //   try {
      //     const res = await fetch(apiURL("jwtRefresh"), { headers: { Authorization: `Bearer ${refreshToken}`, "auth-provider": "custom" } })
      //     const data = await res.json()
      //     if (res.ok) {
      //       Sentry.Browser.addBreadcrumb({
      //         category: "auth",
      //         message: `Completed refresh`,
      //         level: "info",
      //       })
      //       await setAuthData(data)
      //     } else {
      //       console.error("Got a bad response from token refresh", data)

      //       Sentry.Browser.addBreadcrumb({
      //         category: "auth",
      //         message: `Got a failed response from the JSON in the token refresh`,
      //         level: "error",
      //       })

      //       await setAuthData(undefined)
      //     }
      //   } catch (error) {
      //     console.error("Got a bad response from token refresh", error)

      //     Sentry.Browser.addBreadcrumb({
      //       category: "auth",
      //       message: `Got a failed response from token refresh`,
      //       level: "error",
      //     })

      //     await setAuthData(undefined)
      //   }

      //   resolve(undefined)
      // }).then(() => {
      //   refreshPromise = undefined
      // })
      return Promise.resolve(undefined)
    },

    getToken: async () => {
      if (!authData) {
        debugger
        throw new Error("No user data to extract")
      }
      const token = authData.token
      try {
        const decoded = jwt_decode<JwtPayload & object>(token)
        if (!decoded) {
          await setAuthData(undefined)
          console.error("Could not decode the access token - logging out", token)
          throw Error("Invalid access token")
        }
        return token
      } catch (error) {
        await setAuthData(undefined)
        console.error("Could not decode the access token - logging out", token)
        throw Error("Invalid access token")
        return undefined
      }
    },

    getTokenContents: async () => {
      if (!authData) {
        debugger
        throw new Error("No user data to extract")
      }

      const token = authData.token
      try {
        const decoded = jwt_decode<JwtPayload & object>(token)
        if (!decoded) {
          console.error("Could extract token contents - logging out", token)

          await setAuthData(undefined)
          throw Error("Invalid access token")
        }
        return decoded
      } catch (error) {
        await setAuthData(undefined)
        console.error("Could not decode the access token - logging out", token)
        throw Error("Invalid access token")
        return undefined
      }
    },

    getUserMetadata: () => authData!,
    getUserID: () => userIDOrAnonID,
  }
}
